import React, { useState } from 'react';
import { createRoot } from 'react-dom/client';
import { Modal } from 'react-bootstrap';
import Icon from '../../common/icons';
import { clearWaitingAnswers } from './answerOutbox';
import { waitingSummary } from './signOutAnswers';

// "Sign out now?" when test answers are still waiting on this device to reach the school (F21).
// Sign out with nothing waiting goes ahead at once. Going ahead anyway clears the waiting
// answers (the automatic sign-outs keep them on a personal phone; see signOutAnswers.js).
// Student dialog: the yellow Terms-dialog skin (styles/modals.css, am- parts).

const NAMES = { riasec: 'Career interests', dass42: 'Mood and stress', ryff: 'Well-being', sdq: 'Strengths and difficulties' };

export const SignOutConfirmDialog = ({ show, count, tests = [], onStay, onSignOut }) => {
  const names = tests.map((type) => NAMES[type]).filter(Boolean);
  const what = names.length === 1 ? ` from your ${names[0]} test` : '';
  return (
    <Modal show={show} onHide={onStay} centered className="ts-signout-modal">
      <Modal.Header closeButton>
        <Modal.Title>Sign out now?</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="am-intro am-intro-only">
          <span className="am-intro-icon is-warning">
            <Icon name="clock" size={28} />
          </span>
          <h3 className="am-intro-title">
            {count === 1 ? '1 answer' : `${count} answers`}
            {what} {count === 1 ? "hasn't" : "haven't"} reached the guidance office yet
          </h3>
          <p className="am-intro-text">
            They&apos;re waiting on this device for the internet. If you sign out now, they&apos;ll be
            removed and you&apos;ll answer those questions again next time. Stay signed in and
            they&apos;ll send on their own once you&apos;re online.
          </p>
        </div>
      </Modal.Body>
      <Modal.Footer className="am-foot-split">
        <button type="button" className="am-btn is-soft" onClick={onSignOut}>
          Sign out anyway
        </button>
        <button type="button" className="am-btn is-primary" onClick={onStay}>
          <Icon name="check" size={18} />
          Stay signed in
        </button>
      </Modal.Footer>
    </Modal>
  );
};

let host = null;

const Host = ({ summary, onStay, onSignOut }) => {
  const [show, setShow] = useState(true);
  const close = (then) => {
    setShow(false);
    then();
  };
  return (
    <SignOutConfirmDialog
      show={show}
      count={summary.count}
      tests={summary.tests}
      onStay={() => close(onStay)}
      onSignOut={() => close(onSignOut)}
    />
  );
};

const unmount = () => {
  if (!host) return;
  const { root, node } = host;
  host = null;
  // Let the dialog fade out first
  window.setTimeout(() => {
    root.unmount();
    node.remove();
  }, 400);
};

/**
 * Sign out, asking first when answers are still waiting on this device. `signOut` is the
 * page's own sign-out (App.handleLogout, LandingPage.handleLogout).
 */
export const requestSignOut = (signOut) => {
  const summary = waitingSummary();
  if (!summary.count || typeof document === 'undefined') {
    signOut();
    return;
  }
  if (host) return;
  const node = document.createElement('div');
  document.body.appendChild(node);
  const root = createRoot(node);
  host = { root, node };
  root.render(
    <Host
      summary={summary}
      onStay={unmount}
      onSignOut={() => {
        clearWaitingAnswers();
        unmount();
        signOut();
      }}
    />,
  );
};

export default requestSignOut;
